import { showLink } from "./link";
import { keepReturnPage, takeReturnPage } from "./shell/idle";
import { dropMe, keepMe, keptMe } from "./shell/me-cache";
import { startShell } from "./shell/shell";
import type { Me } from "./shell/types";
import { api, el, startTips } from "./ui/dom";
import { icon } from "./ui/icons";
import { poweredBy } from "./ui/powered";

interface Status {
  app: string;
  version: string;
  commit: string;
  db: "ok" | "down";
  migrations: string | null;
}

// Signed out: the sign-in box. Signed in: the app shell (app/shell/shell.ts). A secure link
// (/s/<token>, brief 1B-4) opens its own page without signing in (app/link.ts).

const view = document.getElementById("view") as HTMLElement;

// ---- Signed out: email first, then Google or a one-time code ---------------------------------

const NOTICES: Record<string, string> = {
  failed: "Sign-in failed for that email. Check it and try again, or ask the office for access.",
  unavailable: "Sign-in isn't set up yet.",
  idle: "Signed out after {n} idle. Sign in again to open the same page.",
};

function notice(text: string): HTMLElement {
  return el("p", { class: "note", role: "alert" }, icon("alert"), " ", text);
}

/** The sign-in box sits in the page's main area, centered (mockup sign-in). */
function page(card: HTMLElement) {
  view.replaceChildren(el("main", { class: "signin-page" }, card, poweredBy("p")));
}

function showSignIn(message?: string, disabled = false) {
  const input = el("input", {
    type: "email",
    id: "email",
    name: "email",
    autocomplete: "email",
    inputmode: "email",
    required: "",
    placeholder: "name@example.com",
  });
  const submit = el(
    "button",
    { class: "btn pri", type: "submit" },
    "Continue ",
    icon("arrow-right"),
  );
  const form = el(
    "form",
    { class: "form", novalidate: "" },
    el("div", { class: "field" }, el("label", { for: "email" }, "Email"), input),
    submit,
  );
  const card = el(
    "section",
    { class: "card signin" },
    el("h1", {}, "Sign in"),
    ...(message ? [notice(message)] : []),
    form,
    el(
      "p",
      { class: "small muted" },
      "Enter a work email. Sign-in continues with Google or a one-time code by email.",
    ),
  );
  if (disabled) {
    input.disabled = true;
    submit.disabled = true;
  }
  page(card);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    submit.disabled = true;
    try {
      const res = await fetch("/api/auth/start", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: input.value }),
      });
      if (res.status === 503) return showSignIn(NOTICES.unavailable, true);
      const body = (await res.json()) as {
        method?: "google" | "code";
        redirect?: string;
        error?: string;
      };
      if (!res.ok || !body.redirect || !body.method) {
        submit.disabled = false;
        card.querySelector(".note")?.remove();
        card.insertBefore(notice("Enter an email address."), form);
        return;
      }
      showWayIn(input.value.trim(), body.method, body.redirect);
    } catch {
      submit.disabled = false;
    }
  });
}

function showWayIn(email: string, method: "google" | "code", redirect: string) {
  const go = el(
    "a",
    { class: "btn pri", href: redirect },
    ...(method === "google"
      ? ["Continue with Google ", icon("arrow-right")]
      : [icon("mail"), " Email me a code"]),
  );
  const back = el("button", { class: "btn ghost", type: "button" }, "Use a different email");
  back.addEventListener("click", () => showSignIn());
  page(
    el(
      "section",
      { class: "card signin" },
      el("h1", {}, "Sign in"),
      el(
        "p",
        { class: "muted" },
        method === "google"
          ? "Sign-in continues with the Google account "
          : "A one-time code goes by email to ",
        el("b", {}, email),
        ".",
      ),
      el("div", { class: "row" }, go, back),
    ),
  );
  go.focus();
}

async function start() {
  const link = /^\/s\/([A-Za-z0-9_-]+)\/?$/.exec(location.pathname);
  if (link) return showLink(view, link[1] as string);
  const params = new URLSearchParams(location.search);
  const signin = params.get("signin");
  if (signin) history.replaceState(null, "", "/");
  // Through the one request helper (brief 1E-27): a first answer lost on the way is asked for
  // again, and no answer at all shows the sign-in page's notice within seconds.
  const asked = api<Me & { idle?: unknown }>("GET", "/api/me", undefined, { app: false });
  // Signed in earlier in this visit (brief 1E-29): the shell and the screen draw at once from what
  // this tab remembers, while /api/me is asked again alongside the screen's own data.
  const kept = signin ? null : keptMe();
  if (kept) {
    document.querySelector(".foot")?.remove();
    return startShell(kept, asked.then(signedOutUnlessOk));
  }
  const res = await asked;
  if (res.ok) {
    document.querySelector(".foot")?.remove();
    keepMe(res.body);
    // Signed in again after an idle sign-out (brief 1E-15): back to the same page.
    const back = takeReturnPage();
    if (back) history.replaceState(null, "", back);
    return startShell(res.body);
  }
  dropMe();
  // Move day (brief 1E-31): the server's maintenance line instead of the usual notice.
  const moving = (res.body as { maintenance?: unknown; error?: unknown } | null) ?? null;
  if (res.status === 503 && moving?.maintenance === true && typeof moving.error === "string")
    return showSignIn(moving.error, true);
  if (res.status === 0 || res.status >= 500) return showSignIn(NOTICES.unavailable, true);
  // Signed out on a page (the server's idle sign-out with the tab closed, brief 1E-19c): signing
  // in opens that page again.
  if (!signin) keepReturnPage(location.pathname + location.search);
  const out = res.body;
  const minutes = typeof out.idle === "number" ? String(out.idle) : params.get("m");
  const why = signin ?? (typeof out.idle === "number" ? "idle" : null);
  showSignIn(
    why
      ? NOTICES[why]?.replace(
          "{n}",
          // Brief 1E-30: any whole minutes, 1 to 1440.
          minutes && /^\d{1,4}$/.test(minutes)
            ? `${minutes} minute${minutes === "1" ? "" : "s"}`
            : "some minutes",
        )
      : undefined,
  );
}

/**
 * The fresh /api/me behind a remembered one: its answer when signed in. Signed out meanwhile (the
 * server's idle sign-out, a session ended elsewhere): the sign-in page, with the idle notice when
 * that was why, and this page opened again after signing in. No answer: the remembered one stays.
 */
function signedOutUnlessOk(res: Awaited<ReturnType<typeof api<Me & { idle?: unknown }>>>) {
  if (res.ok) return res.body;
  if (res.status === 0 || res.status >= 500) return null;
  dropMe();
  keepReturnPage(location.pathname + location.search);
  const idle = res.body?.idle;
  location.replace(typeof idle === "number" ? `/?signin=idle&m=${idle}` : "/");
  return null;
}

// ---- Footer status ---------------------------------------------------------------------------

// Brief 1E-30: nothing about builds or the database on screen. The footer says only when FlowCare
// can't be reached; the version and commit stay in /api/status for the checks.
async function showStatus(el: HTMLElement): Promise<void> {
  try {
    const res = await fetch("/api/status", { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const s = (await res.json()) as Status;
    el.dataset.db = s.db;
    el.dataset.commit = s.commit;
    el.textContent =
      s.db === "ok" ? "" : "FlowCare isn't answering right now. Try again in a minute.";
  } catch {
    el.dataset.db = "down";
    el.textContent = "FlowCare isn't answering right now. Try again in a minute.";
  }
}

startTips();
void start();
const statusEl = document.getElementById("status");
if (statusEl) void showStatus(statusEl);
